import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  executeBkashPayment,
  getPaymentHistory,
  initiateBkashPayment,
  submitBkashPayment,
  uploadBankSlip,
  type BankSlipUploadFields,
  type PaymentHistoryParams,
} from '../../infrastructure/api/paymentsApi';
import type { BkashPaymentInitiateDto, BkashSubmitDto } from '../../domain/contracts/api.contracts';
import { qKeys } from './keys';
import { queryDefaults } from './queryPolicy';

/** E18 — GET /payments/history (paged + channel/status/date filters). */
export function usePaymentHistory(params: PaymentHistoryParams) {
  return useQuery({
    queryKey: qKeys.payments.history(params),
    queryFn: () => getPaymentHistory(params),
    ...queryDefaults,
    placeholderData: (prev) => prev,
  });
}

/** E13 — bank-slip multipart upload; invalidates history + dashboard on success. */
export function useBankSlipUpload() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ fields, file }: { fields: BankSlipUploadFields; file: File }) =>
      uploadBankSlip(fields, file),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qKeys.payments.all });
      void queryClient.invalidateQueries({ queryKey: qKeys.dashboard });
    },
  });
}

/** POST /payments/bkash/submit — manual TrxID submission; refreshes payments, subscription and dashboard. */
export function useSubmitBkashPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (req: BkashSubmitDto) => submitBkashPayment(req),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qKeys.payments.all });
      void queryClient.invalidateQueries({ queryKey: qKeys.subscription });
      void queryClient.invalidateQueries({ queryKey: qKeys.dashboard });
    },
  });
}

/** @deprecated E11/E12 redirect checkout is not built; the page uses `useSubmitBkashPayment`. */
export function useBkashCheckout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (req: BkashPaymentInitiateDto) => {
      const init = await initiateBkashPayment(req);
      const payment = await executeBkashPayment(init.paymentID);
      return payment;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qKeys.payments.all });
      void queryClient.invalidateQueries({ queryKey: qKeys.subscription });
      void queryClient.invalidateQueries({ queryKey: qKeys.dashboard });
    },
  });
}

